import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Copy, Check, RefreshCw, Pencil } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { generateExample, type FieldMapping } from '@/lib/admin/field-utils';

interface ExampleGeneratorProps {
  fields: FieldMapping[];
  customExample: string;
  onCustomExampleChange: (value: string) => void;
}

export function ExampleGenerator({ fields, customExample, onCustomExampleChange }: ExampleGeneratorProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const generatedExample = useMemo(() => {
    if (fields.length === 0) return null;
    return generateExample(fields);
  }, [fields]);

  const displayJson = useMemo(() => {
    if (customExample) {
      try {
        return JSON.stringify(JSON.parse(customExample), null, 2);
      } catch {
        return customExample;
      }
    }
    if (!generatedExample) return '';
    return JSON.stringify(generatedExample, null, 2);
  }, [customExample, generatedExample]);

  const handleCopy = async () => {
    if (!displayJson) return;
    await navigator.clipboard.writeText(displayJson);
    setCopied(true);
    toast({ title: 'Example copied to clipboard' });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerate = () => {
    onCustomExampleChange('');
    setIsEditing(false);
    toast({ title: 'Example regenerated from fields' });
  };

  const handleEdit = () => {
    if (!isEditing && !customExample) {
      onCustomExampleChange(displayJson);
    }
    setIsEditing(!isEditing);
  };

  const handleExampleChange = (value: string) => {
    onCustomExampleChange(value);
  };

  if (fields.length === 0) {
    return (
      <div className="border-2 border-dashed rounded-lg p-8 text-center">
        <p className="text-muted-foreground">Add fields to generate an example</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div>
          <h4 className="text-sm font-medium">Example Data</h4>
          <p className="text-xs text-muted-foreground">
            {customExample ? 'Custom example' : 'Auto-generated from fields'}
          </p>
        </div>
        <div className="flex gap-2">
          {customExample && (
            <Button variant="outline" size="sm" onClick={handleRegenerate}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Regenerate
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleEdit}>
            <Pencil className="h-4 w-4 mr-1" />
            {isEditing ? 'Done' : 'Edit'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleCopy}>
            {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </div>

      {isEditing ? (
        <Textarea
          value={customExample || displayJson}
          onChange={(e) => handleExampleChange(e.target.value)}
          className="font-mono text-sm min-h-[400px]"
          placeholder='{"id": "...", "name": "..."}'
        />
      ) : (
        <ScrollArea className="h-[400px] border rounded-lg">
          <pre className="p-4 text-sm font-mono whitespace-pre overflow-x-auto bg-muted/30">
            {displayJson}
          </pre>
        </ScrollArea>
      )}
    </div>
  );
}
